import React from "react";
import * as Select from "@radix-ui/react-select";
import { TbChevronDown, TbCheck } from "react-icons/tb";
import { clsx } from "clsx";

interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface CustomSelectProps {
  value?: string;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  options: SelectOption[];
  disabled?: boolean;
  hasError?: boolean;
  className?: string;
}

export const CustomSelect = React.forwardRef<
  React.ElementRef<typeof Select.Trigger>,
  CustomSelectProps
>(
  (
    {
      value,
      onValueChange,
      placeholder = "Select",
      options,
      disabled = false,
      hasError = false,
      className,
      ...props
    },
    ref
  ) => {
    return (
      <Select.Root
        value={value || undefined}
        onValueChange={onValueChange}
        disabled={disabled}
      >
        <Select.Trigger
          ref={ref}
          className={clsx(
            "flex h-10 w-full items-center justify-between rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground",
            "focus:outline-none focus:bg-white",
            "disabled:cursor-not-allowed disabled:opacity-50",
            "data-[placeholder]:text-gray-500",
            hasError && "border-red-400",
            className
          )}
          {...props}
        >
          <Select.Value placeholder={placeholder} />
          <Select.Icon asChild>
            <TbChevronDown className="h-4 w-4 opacity-50" />
          </Select.Icon>
        </Select.Trigger>
        <Select.Portal>
          <Select.Content className="relative z-[1002] max-h-96 min-w-[8rem] overflow-hidden rounded-lg border border-gray-200 bg-white text-popover-foreground shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2">
            <Select.ScrollUpButton className="flex cursor-default items-center justify-center py-1">
              <TbChevronDown className="h-4 w-4 rotate-180" />
            </Select.ScrollUpButton>
            <Select.Viewport className="p-1">
              {options
                .filter((option) => option.value !== "") // Filter out empty string values
                .map((option) => (
                  <Select.Item
                    key={option.value}
                    value={option.value}
                    disabled={option.disabled}
                    className="relative flex w-full cursor-default select-none items-center rounded-md py-2 pl-8 pr-2 text-sm outline-none hover:bg-gray-100 focus:bg-gray-100 data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
                  >
                    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                      <Select.ItemIndicator>
                        <TbCheck className="h-4 w-4" />
                      </Select.ItemIndicator>
                    </span>
                    <Select.ItemText>{option.label}</Select.ItemText>
                  </Select.Item>
                ))}
            </Select.Viewport>
            <Select.ScrollDownButton className="flex cursor-default items-center justify-center py-1">
              <TbChevronDown className="h-4 w-4" />
            </Select.ScrollDownButton>
          </Select.Content>
        </Select.Portal>
      </Select.Root>
    );
  }
);

CustomSelect.displayName = "CustomSelect";
